"use client"

import React from 'react'
import { yesNoOptions } from '../../constants/options'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form'
import { Control, FieldPath, FieldValues } from 'react-hook-form'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { cn } from '../../lib/utils'
import { InputGroupProps } from './form-input-wrapper'
import { useDirtyFields } from '../../../features/homeowners/context/dirty-fields-context'
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'

/**
 * Props for the FormYesNoInput component
 * @property {string} name - The field name 
 * @property {InputGroupProps} [inputGroupProps] - Optional props for customizing the input group styling and label
 * @property {string} [placeholder] - Optional hint when no selection (e.g. "Select") so the control isn't blank
 */
type FormYesNoInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
    name: TName,
    control: Control<TFieldValues>,
    inputGroupProps?: InputGroupProps,
    placeholder?: string,
}

/**
 * FormYesNoInput Component
 * Renders a radio group with Yes/No options that integrates with react-hook-form.
 * The component handles string values ("yes"/"no") for display and saves string values
 * when the user makes a selection. If no option is selected, the field remains undefined
 * which can be interpreted as "unknown" by the backend.
 */
function FormYesNoInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ 
    name, 
    control,
    inputGroupProps,
    placeholder,
}: FormYesNoInputProps<TFieldValues, TName>) {
    // Get dirty field state
    const { isFieldDirty } = useDirtyFields()
    const isDirty = isFieldDirty(name as string)
    
    // Check read-only context
    const { isReadOnly } = useReadOnly()
    
    return (
        <FormField
            control={control}
            name={name}
            render={({ field, fieldState }) => {
                /**
                 * Converts the string field value to a string value for the radio group
                 * @returns "yes" for "yes", "no" for "no", undefined for null/undefined/unknown
                 */
                const getValue = () => {
                    if(field.value === "yes") {
                        return "yes"
                    }
                    if(field.value === "no") {
                        return "no"
                    }
                    // Return undefined for "unknown", null, or undefined values
                    return undefined
                }
                const hasValue = getValue() != null
                const showEmptyHighlight = !!inputGroupProps?.highlightWhenEmpty && !hasValue
                return (
                    <FormItem className={cn("space-y-1", inputGroupProps?.wrapperClassName)}>
                        {inputGroupProps?.label && 
                            <FormLabel 
                                hideError
                                className={cn(
                                    "transition-colors duration-200",
                                    isDirty && "text-[#9c27b0]",
                                    inputGroupProps?.labelClassName
                                )}
                            >
                                {inputGroupProps.label}
                            </FormLabel>
                        }
                        {placeholder && !hasValue && (
                            <p className="text-sm text-muted-foreground">{placeholder}</p>
                        )}
                        <FormControl>
                            <RadioGroup
                                // Handle value changes and save string values
                                onValueChange={value => {
                                    if (!isReadOnly) {
                                        if(value === "yes") {
                                            field.onChange("yes")
                                        }
                                        if(value === "no") {
                                            field.onChange("no")
                                        }
                                    }
                                }}
                                value={getValue()}
                                disabled={isReadOnly}
                                className={cn(
                                    "flex gap-5 py-1.5 px-1 rounded-md border border-transparent transition-colors duration-200",
                                    showEmptyHighlight && "!border-[var(--color-pizza-600)]",
                                )}
                                style={showEmptyHighlight ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" } : undefined}
                                ref={field.ref}
                            >
                                {/* Map through yes/no options to create radio buttons */}
                                {yesNoOptions.map((option, index) => (
                                    <FormItem
                                        className={"flex items-center gap-1 space-y-0"}
                                        key={`${option.value}-radio-option-key-${index}`}
                                    >
                                        <FormControl>
                                            <RadioGroupItem
                                                value={option.value}
                                            />
                                        </FormControl>
                                        <FormLabel className="font-normal cursor-pointer" hideError>
                                            {option.label}
                                        </FormLabel>
                                    </FormItem>
                                ))}
                            </RadioGroup>
                        </FormControl>
                    <FormMessage />
                </FormItem>
            )}}
        />
    )
}

export default FormYesNoInput